import React from 'react';
import styled, { keyframes } from 'styled-components';
import PickerHeader from '../PickerHeader';
import { useMutation } from '@apollo/client';
import { editProcedurePriority } from '../../../../../graph/procedures';
import Troubleshooting from '../../tablet/Troubleshooting';

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.backgroundColor.string()};
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 0 1em 1em;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const PrioritySlots = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  grid-auto-rows: min-content;
  overflow-y: auto;
  align-content: start;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SlotSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #ffffff;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: ${spin} 0.8s linear infinite;
`;

const PrioritySlotWrapper = styled.button<{ selected?: boolean }>`
  border: none;
  color: inherit;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => (props.selected ? '#00b641' : 'rgba(255, 255, 255, 0.1)')};
  font-size: 4rem;
  border-radius: 0.25rem;
  aspect-ratio: 1;
  min-height: 120px;
  cursor: pointer;

  &:active {
    background: #00b641;
  }

  &:disabled {
    opacity: ${props => (props.selected ? 1 : 0.25)};
    background: ${props => (props.selected ? '#00b641' : 'rgba(255, 255, 255, 0.05)')};
    color: ${props => (props.selected ? 'inherit' : 'rgba(255, 255, 255, 0.3)')};
    cursor: not-allowed;
  }
`;

const PrioritySlot = ({
  procedureId,
  priority,
  disabled,
  onClick,
  selected,
  showSpinner,
}: {
  procedureId: number;
  priority: number;
  disabled: boolean;
  onClick: (priority: number, procedureId: number) => void;
  selected?: boolean;
  showSpinner?: boolean;
}) => (
  <PrioritySlotWrapper
    selected={selected}
    disabled={disabled}
    onClick={() => !disabled && onClick(priority, procedureId)}
  >
    {showSpinner ? <SlotSpinner /> : <span>{priority}</span>}
  </PrioritySlotWrapper>
);

const PreOpProcedurePriorityPicker = ({
  procedureId,
  assignedPriority,
  goToStartPage,
  onCancel,
  roomName,
}: {
  procedureId: number;
  assignedPriority?: number;
  goToStartPage: () => void;
  onCancel: () => void;
  roomName: string;
}) => {
  const [edit, { loading }] = useMutation(editProcedurePriority);
  const [selectedPriority, setSelectedPriority] = React.useState<number | null>(null);

  const handleAssignPriority = async (priority: number, procedureIdArg: number) => {
    if (loading) {
      return;
    }

    try {
      setSelectedPriority(priority);
      await edit({
        variables: {
          id: procedureIdArg,
          procedurePriority: priority,
        },
      });
      goToStartPage();
    } catch (error) {
      console.error('Error assigning priority:', error);
    }
  };

  return (
    <FullScreenOverlay>
      <Content>
        <PickerHeader onCancel={onCancel} title={'Procedure Priority'} bedInput={undefined} />
        <PrioritySlots>
          {Array.from(Array(10).keys()).map((p: number) => (
            <PrioritySlot
              key={p}
              disabled={loading || p + 1 === assignedPriority}
              priority={p + 1}
              procedureId={procedureId}
              onClick={handleAssignPriority}
              selected={p + 1 === assignedPriority}
              showSpinner={loading && selectedPriority === p + 1}
            />
          ))}
        </PrioritySlots>
      </Content>
      <Troubleshooting roomName={roomName} />
    </FullScreenOverlay>
  );
};

export default PreOpProcedurePriorityPicker;
